<template>
    <div :class="['last_viewed_client_wrapper', { 'is-dragging': isDragging, 'is-scrollable': isScrollable }]"
         v-show="lastViewedClients.length > 0"
         ref="wrapperRef"
         @mouseleave="onWrapperLeave"
         @wheel="onWheel"
         @pointerdown="onPointerDown"
         @pointermove="onPointerMove"
         @pointerup="onPointerUp"
         @pointercancel="onPointerCancel">
        <transition-group tag="div"
                          name="tab"
                          class="last_viewed_client_inner"
                          ref="innerRef">
            <div :class="lastViewedClient.id == currentClientId ? 'last_viewed_client last_viewed_client_active' : 'last_viewed_client'"
                 :style="tabStyle(lastViewedClient, index)"
                 v-for="(lastViewedClient, index) in lastViewedClients"
                 :key="lastViewedClient.id"
                 @auxclick="onAuxClick($event, index)"
                 @mousedown.middle.prevent
                 @mouseenter="onTabEnter($event, lastViewedClient, index)"
                 @mouseleave="onTabLeave">
                <p class="last_viewed_client_p" @click="onClick(lastViewedClient.id)">
                    <span v-if="lastViewedClient.contractStatus != null"
                          :class="'dot dot_contract_' + getContractStatus(lastViewedClient.contractStatus)"></span>{{lastViewedClient.fi}}
                </p>
                <div class="last_viewed_client_img_wrapper" @click="onDelete(index)">
                    <img class="last_viewed_client_img" src="/assets/img/close.svg" />
                </div>
            </div>
        </transition-group>
    </div>
    <teleport to="body">
        <div v-if="hoveredTab"
             :class="['last_viewed_client_hover_floating', { last_viewed_client_hover_end_floating: hoveredIsEnd }]"
             :style="tooltipStyle">
            <div>{{ hoveredTab.title }}</div>
            <div v-if="hoveredTab.contractName" class="flex last_viewed_client_hover_contract">
                <span>{{ hoveredTab.contractName }}</span>
                <span :class="'dot dot_contract_' + getContractStatus(hoveredTab.contractStatus)"></span>
                <span>{{ $t('contract_status.' + getContractStatus(hoveredTab.contractStatus)) }}</span>
            </div>
        </div>
    </teleport>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';
    import { getContractStatus } from '../../services/helper';
    import { LastViewedClients } from '../../models/Clients';

    const DRAG_THRESHOLD_PX = 5;

    export default defineComponent({
        name: 'VLastViewedClientsTabs',
        props: {
            currentClientId: {
                type: Number,
                required: false,
                default: null
            }
        },
        emits: ['navigate'],
        data() {
            return {
                frozen: false,
                frozenWidths: null as Record<number, number> | null,
                isDragging: false,
                dragPointerDown: false,
                dragStartX: 0,
                dragStartScrollLeft: 0,
                wasDragged: false,
                dragPointerId: null as number | null,
                pendingLeave: false,
                hoveredTab: null as null | {
                    title: string;
                    contractName: string | null;
                    contractStatus: number | null;
                },
                hoveredRect: null as DOMRect | null,
                hoveredIsEnd: false,
                isScrollable: false,
                resizeObserver: null as ResizeObserver | null
            };
        },
        computed: {
            lastViewedClients(): LastViewedClients[] {
                return this.$store.getters.getLastViewedClients;
            },
            tooltipStyle(): Record<string, string> {
                if (!this.hoveredRect) return { display: 'none' };
                const r = this.hoveredRect;
                const base: Record<string, string> = {
                    position: 'fixed',
                    bottom: `${window.innerHeight - r.top + 2}px`
                };
                if (this.hoveredIsEnd) {
                    base.right = `${window.innerWidth - r.right}px`;
                    base.left = 'auto';
                } else {
                    base.left = `${r.left + 8}px`;
                }
                return base;
            }
        },
        mounted() {
            this.updateScrollable();
            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => this.updateScrollable());
                const wrapper = this.$refs.wrapperRef as HTMLElement | undefined;
                if (wrapper instanceof Element) this.resizeObserver.observe(wrapper);
                const inner = (this.$refs.innerRef as { $el?: HTMLElement } | undefined)?.$el;
                if (inner instanceof Element) this.resizeObserver.observe(inner);
            }
        },
        beforeUnmount() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
        },
        watch: {
            'lastViewedClients.length'(newLen: number, oldLen: number) {
                if (this.frozen && newLen > oldLen) {
                    this.frozen = false;
                    this.frozenWidths = null;
                }
                this.$nextTick(() => this.updateScrollable());
            },
            currentClientId(newId: number | null, oldId: number | null) {
                if (newId === oldId || newId === null || this.frozen) return;
                const wrapper = this.$refs.wrapperRef as HTMLElement | undefined;
                if (wrapper && typeof wrapper.scrollTo === 'function') {
                    wrapper.scrollLeft = 0;
                }
            }
        },
        methods: {
            getContractStatus,
            tabStyle(client: LastViewedClients, index: number) {
                const base: Record<string, string | number> = { zIndex: this.lastViewedClients.length - index };
                if (this.frozen && this.frozenWidths) {
                    const w = this.frozenWidths[client.id];
                    if (w) {
                        base.flex = `0 0 ${w}px`;
                    }
                }
                return base;
            },
            unfreeze() {
                if (!this.frozen) return;
                this.frozen = false;
                this.frozenWidths = null;
            },
            updateScrollable() {
                const wrapper = this.$refs.wrapperRef as HTMLElement | undefined;
                if (!wrapper) return;
                this.isScrollable = wrapper.scrollWidth > wrapper.clientWidth;
            },
            onClick(id: number) {
                if (this.wasDragged) return;
                if (id == this.currentClientId) return;
                this.$emit('navigate', id);
                this.$store.commit('UNSHIFT_LAST_VIEWED_CLIENT', id);
                this.$router.push('/client/' + id);
            },
            onDelete(index: number) {
                if (this.wasDragged) return;
                const tabs = this.lastViewedClients;
                const isDeletingActive = tabs[index].id == this.currentClientId;

                if (!this.frozen) {
                    const innerEl = (this.$refs.innerRef as { $el?: HTMLElement } | undefined)?.$el;
                    if (innerEl) {
                        const tabEls = innerEl.querySelectorAll<HTMLElement>(':scope > .last_viewed_client');
                        const widths: Record<number, number> = {};
                        tabs.forEach((c, i) => {
                            if (tabEls[i]) widths[c.id] = tabEls[i].offsetWidth;
                        });
                        this.frozenWidths = widths;
                    }
                    this.frozen = true;
                }

                if (isDeletingActive) {
                    if (tabs.length == 1) {
                        this.$router.push('/clients');
                    } else {
                        const nextId = tabs[index === 0 ? 1 : 0].id;
                        this.$emit('navigate', nextId);
                        this.$router.push('/client/' + nextId);
                    }
                }

                this.$store.commit('SET_REMOVE_LAST_VIEWED_CLIENT', index);
            },
            onAuxClick(e: MouseEvent, index: number) {
                if (e.button !== 1) return;
                e.preventDefault();
                this.onDelete(index);
            },
            onWrapperLeave() {
                this.hoveredTab = null;
                this.hoveredRect = null;
                if (this.isDragging || this.dragPointerDown) {
                    this.pendingLeave = true;
                    return;
                }
                this.unfreeze();
            },
            onWheel(e: WheelEvent) {
                const wrapper = e.currentTarget as HTMLElement;
                if (wrapper.scrollWidth <= wrapper.clientWidth) return;
                if (e.deltaY === 0) return;
                e.preventDefault();
                wrapper.scrollLeft += e.deltaY;
                this.hoveredTab = null;
                this.hoveredRect = null;
            },
            onTabEnter(e: MouseEvent, client: LastViewedClients, index: number) {
                if (this.isDragging || this.dragPointerDown) return;
                if (client.id == this.currentClientId) return;
                const target = e.currentTarget as HTMLElement;
                this.hoveredRect = target.getBoundingClientRect();
                this.hoveredIsEnd = index > 7;
                this.hoveredTab = {
                    title: client.contractAgreement || client.fi,
                    contractName: client.contractName ?? null,
                    contractStatus: client.contractStatus ?? null
                };
            },
            onTabLeave() {
                this.hoveredTab = null;
                this.hoveredRect = null;
            },
            onPointerDown(e: PointerEvent) {
                if (e.button !== 0) return;
                if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) return;
                const target = e.target as HTMLElement | null;
                if (target && target.closest('.last_viewed_client_img_wrapper')) return;
                const wrapper = e.currentTarget as HTMLElement;
                if (wrapper.scrollWidth <= wrapper.clientWidth) return;

                this.dragStartX = e.clientX;
                this.dragStartScrollLeft = wrapper.scrollLeft;
                this.dragPointerDown = true;
                this.wasDragged = false;
                this.dragPointerId = e.pointerId;
            },
            onPointerMove(e: PointerEvent) {
                if (!this.dragPointerDown) return;
                const dx = e.clientX - this.dragStartX;
                const wrapper = e.currentTarget as HTMLElement;

                if (!this.isDragging && Math.abs(dx) >= DRAG_THRESHOLD_PX) {
                    this.isDragging = true;
                    this.wasDragged = true;
                    this.hoveredTab = null;
                    this.hoveredRect = null;
                    if (this.dragPointerId !== null) {
                        try { wrapper.setPointerCapture(this.dragPointerId); } catch (_) { /* noop */ }
                    }
                }

                if (this.isDragging) {
                    wrapper.scrollLeft = this.dragStartScrollLeft - dx;
                    e.preventDefault();
                }
            },
            onPointerUp(e: PointerEvent) {
                this.finishPointerInteraction(e);
            },
            onPointerCancel(e: PointerEvent) {
                this.finishPointerInteraction(e);
            },
            finishPointerInteraction(e: PointerEvent) {
                if (!this.dragPointerDown) return;
                const wrapper = e.currentTarget as HTMLElement;
                if (this.dragPointerId !== null && wrapper.hasPointerCapture && wrapper.hasPointerCapture(this.dragPointerId)) {
                    try { wrapper.releasePointerCapture(this.dragPointerId); } catch (_) { /* noop */ }
                }
                this.dragPointerDown = false;
                this.isDragging = false;
                this.dragPointerId = null;

                if (this.pendingLeave) {
                    this.pendingLeave = false;
                    this.unfreeze();
                }

                setTimeout(() => { this.wasDragged = false; }, 0);
            }
        }
    });
</script>
